import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Modal } from '../components/common/Modal'
import { Button } from '../components/common/Button'
import { Input } from '../components/common/Input'
import { LoadingSpinner } from '../components/common/LoadingSpinner'
import {
  usePhilosophies,
  useTheses,
  useCreatePhilosophy,
  useUpdatePhilosophy,
  useSeedExamples,
  useThesisAllocations,
  useThesisHealthScores,
} from '../hooks/useThesis'
import { usePortfolioFilter } from '../contexts/PortfolioFilterContext'
import { ThesisProgressRow } from '../components/strategy/ThesisProgressRow'
import type { PhilosophyCreate, PhilosophyUpdate } from '../types/thesis'
import { HEALTH_STATUS_COLORS, HEALTH_SCORE_COLORS } from '../types/thesis'

export function PhilosophyPage() {
  const { data: philosophies, isLoading } = usePhilosophies()
  const { data: theses } = useTheses()
  const createPhilosophy = useCreatePhilosophy()
  const updatePhilosophy = useUpdatePhilosophy()
  const seedExamples = useSeedExamples()
  const { selectedPortfolioId } = usePortfolioFilter()
  const { data: allocationsData } = useThesisAllocations(selectedPortfolioId)
  const { data: healthData } = useThesisHealthScores(selectedPortfolioId)

  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [formData, setFormData] = useState<PhilosophyCreate>({
    name: '',
    narrative: '',
  })
  const [error, setError] = useState('')

  // Typically users have one philosophy
  const philosophy = philosophies?.[0]
  const philosophyTheses = theses?.filter((t) => t.philosophy_id === philosophy?.id)

  const totalAllocation = philosophyTheses?.reduce(
    (sum, t) => sum + (t.target_allocation_pct ?? 0),
    0
  ) ?? 0

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    try {
      await createPhilosophy.mutateAsync(formData)
      setIsCreateOpen(false)
      setFormData({ name: '', narrative: '' })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create philosophy')
    }
  }

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    if (!philosophy) return
    if (!formData.name.trim()) {
      setError('Name is required')
      return
    }

    try {
      const updateData: PhilosophyUpdate = {
        name: formData.name,
        narrative: formData.narrative,
      }
      await updatePhilosophy.mutateAsync({ id: philosophy.id, data: updateData })
      setIsEditing(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update philosophy')
    }
  }

  const startEditing = () => {
    if (philosophy) {
      setFormData({
        name: philosophy.name,
        narrative: philosophy.narrative ?? '',
      })
      setIsEditing(true)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // No philosophy yet - show creation prompt
  if (!philosophy) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Investment Philosophy</h1>

        <div className="text-center py-12 bg-gray-50 rounded-lg">
          <svg
            className="mx-auto h-12 w-12 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
            />
          </svg>
          <h3 className="mt-2 text-lg font-medium text-gray-900">Define Your Investment Philosophy</h3>
          <p className="mt-1 text-sm text-gray-500 max-w-md mx-auto">
            Your philosophy is your overarching investment worldview. It contains your theses -
            specific beliefs about market opportunities - which in turn have monitoring rules.
          </p>
          <div className="flex justify-center gap-3 mt-4">
            <Button onClick={() => setIsCreateOpen(true)}>
              Create Philosophy
            </Button>
            <Button
              variant="secondary"
              onClick={() => seedExamples.mutate()}
              isLoading={seedExamples.isPending}
            >
              Load Examples
            </Button>
          </div>
          {seedExamples.isSuccess && (
            <p className="text-sm text-green-600 mt-3">
              Examples loaded! Refreshing...
            </p>
          )}
          {seedExamples.isError && (
            <p className="text-sm text-red-600 mt-3">
              {seedExamples.error instanceof Error ? seedExamples.error.message : 'Failed to load examples'}
            </p>
          )}
        </div>

        {/* Create Modal */}
        <Modal
          isOpen={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          title="Create Investment Philosophy"
        >
          <form onSubmit={handleCreate} className="space-y-4">
            <Input
              label="Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Thesis-Driven Contrarian Investing"
              required
            />

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Narrative
              </label>
              <textarea
                value={formData.narrative}
                onChange={(e) => setFormData({ ...formData, narrative: e.target.value })}
                placeholder="Describe your overall investment approach, beliefs, and strategy..."
                rows={6}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
            </div>

            {error && <p className="text-sm text-red-600">{error}</p>}

            <div className="flex justify-end gap-3 pt-4">
              <Button variant="secondary" onClick={() => setIsCreateOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" isLoading={createPhilosophy.isPending}>
                Create
              </Button>
            </div>
          </form>
        </Modal>
      </div>
    )
  }

  // Show existing philosophy
  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{philosophy.name}</h1>
          <p className="text-sm text-gray-500 mt-1">
            {philosophy.theses_count ?? philosophyTheses?.length ?? 0} theses | {totalAllocation}%
            allocated
          </p>
        </div>
        <Button variant="secondary" onClick={startEditing}>
          Edit
        </Button>
      </div>

      {/* Narrative */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-3">Philosophy</h2>
        {philosophy.narrative ? (
          <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-wrap">
            {philosophy.narrative}
          </div>
        ) : (
          <p className="text-gray-400 italic">No narrative defined yet.</p>
        )}
      </div>

      {/* Portfolio Summary */}
      {allocationsData && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Portfolio Value</p>
            <p className="text-xl font-semibold text-gray-900">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(allocationsData.total_portfolio_value)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Allocated</p>
            <p className="text-xl font-semibold text-gray-900">
              {(100 - allocationsData.unallocated_pct).toFixed(1)}%
            </p>
            <p className="text-xs text-gray-400">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(allocationsData.total_allocated_value)}
            </p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
            <p className="text-sm text-gray-500">Unallocated</p>
            <p className="text-xl font-semibold text-gray-900">
              {allocationsData.unallocated_pct.toFixed(1)}%
            </p>
            <p className="text-xs text-gray-400">
              {new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
              }).format(
                allocationsData.total_portfolio_value - allocationsData.total_allocated_value
              )}
            </p>
          </div>
          {healthData && (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <p className="text-sm text-gray-500">Avg Health</p>
              <div className="flex items-center gap-2">
                <p className={`text-xl font-semibold ${
                  healthData.average_health >= 80 ? HEALTH_SCORE_COLORS.excellent :
                  healthData.average_health >= 60 ? HEALTH_SCORE_COLORS.good :
                  healthData.average_health >= 40 ? HEALTH_SCORE_COLORS.caution :
                  healthData.average_health >= 20 ? HEALTH_SCORE_COLORS.poor :
                  HEALTH_SCORE_COLORS.critical
                }`}>
                  {healthData.average_health.toFixed(0)}
                </p>
                <span className={`px-1.5 py-0.5 text-xs font-medium rounded ${
                  healthData.average_health >= 80 ? HEALTH_STATUS_COLORS.excellent :
                  healthData.average_health >= 60 ? HEALTH_STATUS_COLORS.good :
                  healthData.average_health >= 40 ? HEALTH_STATUS_COLORS.caution :
                  healthData.average_health >= 20 ? HEALTH_STATUS_COLORS.poor :
                  HEALTH_STATUS_COLORS.critical
                }`}>
                  {healthData.average_health >= 80 ? 'excellent' :
                   healthData.average_health >= 60 ? 'good' :
                   healthData.average_health >= 40 ? 'caution' :
                   healthData.average_health >= 20 ? 'poor' : 'critical'}
                </span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Theses Summary */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">Theses</h2>
          <Link
            to="/strategy/theses"
            className="text-sm text-primary-600 hover:text-primary-700"
          >
            Manage theses →
          </Link>
        </div>

        {philosophyTheses && philosophyTheses.length > 0 ? (
          <div>
            {philosophyTheses.map((thesis) => {
              const allocation = allocationsData?.allocations.find(
                (a) => a.thesis_id === thesis.id
              )
              const health = healthData?.scores.find((s) => s.thesis_id === thesis.id)
              return (
                <ThesisProgressRow
                  key={thesis.id}
                  thesis={thesis}
                  allocation={allocation}
                  healthScore={health}
                />
              )
            })}
          </div>
        ) : (
          <p className="text-gray-400 text-center py-4">
            No theses yet.{' '}
            <Link to="/strategy/theses" className="text-primary-600 hover:text-primary-700">
              Create your first thesis
            </Link>
          </p>
        )}

        {/* Allocation bar */}
        {philosophyTheses && philosophyTheses.length > 0 && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="flex justify-between text-xs text-gray-500 mb-1">
              <span>Total Allocation</span>
              <span className={totalAllocation > 100 ? 'text-red-600 font-medium' : ''}>
                {totalAllocation}%
              </span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  totalAllocation > 100 ? 'bg-red-500' : 'bg-primary-500'
                }`}
                style={{ width: `${Math.min(totalAllocation, 100)}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Edit Modal */}
      <Modal isOpen={isEditing} onClose={() => setIsEditing(false)} title="Edit Philosophy">
        <form onSubmit={handleUpdate} className="space-y-4">
          <Input
            label="Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
          />

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Narrative</label>
            <textarea
              value={formData.narrative}
              onChange={(e) => setFormData({ ...formData, narrative: e.target.value })}
              rows={8}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>

          {error && <p className="text-sm text-red-600">{error}</p>}

          <div className="flex justify-end gap-3 pt-4">
            <Button variant="secondary" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
            <Button type="submit" isLoading={updatePhilosophy.isPending}>
              Save
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  )
}
